import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { DialogAnimationService } from 'src/app/angular-core/services/dialog-animation.service';
import { BasicXpacInfo, XpacConnectionEventInfo } from 'src/app/generated/CrossPoint.RemoteXpac.Server';
import { DevicesActionsTypes, getDeviceConnectionsAction } from 'src/app/store/actions/devices.actions';
import { selectDeviceConnections } from 'src/app/store/selectors/devices.selectors';
import { hasValue } from 'src/app/angular-core/utils/functions/common.functions';
import { DurationPrecision, getDurationString } from 'src/app/utils/functions/date.functions';
import { IDeviceConnectionsState, IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { compare } from 'src/app/utils/functions/common.functions';
import { getTranslationKeyForValue } from 'src/app/angular-core/utils/functions/translation.functions';

export interface ConnectionData {
  startUtc?: string;  
  endUtc?: string;
  protocol?: string;
  maskedIpAddress?: string;
}

@UntilDestroy()
@Component({
  selector: 'xpac-last-connections',
  templateUrl: './last-connections.component.html',
  styleUrls: ['./last-connections.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LastConnectionsComponent implements OnInit, OnDestroy {

  @Input() public xpac: BasicXpacInfo = null;

  public filteredConnections: ConnectionData[] = [];
  public isLoading: boolean = true;

  private timerHandle: number;

  constructor(private store: Store<IRemoteXPACPortalState>,
    private translate: TranslateService,
    public dialog: DialogAnimationService,
    private cdr: ChangeDetectorRef,
    private actionsListener$: Actions) { }
  
  public ngOnInit(): void {
    this.onInitListners();
    this.store.dispatch(getDeviceConnectionsAction({ data: this.xpac.deviceId }));
  }

  public ngOnDestroy(): void {
    if (hasValue(this.timerHandle)) {
      window.clearInterval(this.timerHandle);
      this.timerHandle = null;
    }
  }

  private initData(): void {
    this.store.select(selectDeviceConnections(this.xpac.deviceId))
      .pipe(
        filter(hasValue),
        untilDestroyed(this),
      )
      .subscribe((connections: IDeviceConnectionsState) => {
        this.filterConnections(connections.events);
      })
  }

  private filterConnections(connectionEvents: XpacConnectionEventInfo[]): void {
    let filteredConnections: ConnectionData[] = [];
    let connection: ConnectionData = {};

    function endConnection(closeEvent: XpacConnectionEventInfo): void {
      if (connection.startUtc || connectionEvents.length === 1) {
        connection.endUtc = closeEvent.timestamp;
        if (closeEvent.maskedIpAddress !== connection.maskedIpAddress) {
          connection.maskedIpAddress += ` - ${closeEvent.maskedIpAddress}`;
        }
        if (closeEvent.protocol !== connection.protocol) {
          connection.protocol += ` - ${closeEvent.protocol}`;
        }
        filteredConnections.unshift(connection);
        connection = {};
      }
    }

    // Create a copy and sort it old to new
    connectionEvents = [...connectionEvents].sort((a, b) => {
      // Timestamps may be slightly out of order if our server instances do not have the exact same time.
      const aTime = new Date(a.timestamp).getTime();
      const bTime = new Date(b.timestamp).getTime();
      if (Math.abs(aTime - bTime) > 500) {
        return compare(aTime, bTime);
      }
      // Two events at the same time. Prefer the order where the disconnect came before the connect.
      // This is the most likely, a new connection was created which instantly closed the old one.
      // Note that the API may return events with the same timestamp in any order and it may not be consistent between calls.
      if (a.event === 'deviceDisconnect' && b.event === 'deviceConnect') {
        return -1;
      }
      else if (b.event === 'deviceDisconnect' && a.event === 'deviceConnect') {
        return 1;
      }
      return 0;
    });

    // Run through the events, oldest to newest, and create connections
    for (let i = 0; i < connectionEvents.length; i++) {
      const event = connectionEvents[i];

      if (event.event === 'deviceDisconnect') {
        endConnection(event);
      }
      else if (event.event === 'deviceConnect') {
        // Make sure any previous connection is closed as they might still be open if the disconnect wasn't clean
        endConnection(event);
        // Start new connection
        connection.startUtc = event.timestamp;
        connection.maskedIpAddress = event.maskedIpAddress;
        connection.protocol = event.protocol;
      }
    }

    // Add the currently active connection if there is one
    if (connection.startUtc) {
      filteredConnections.unshift(connection);
    }

    this.filteredConnections = filteredConnections;

    // Make sure a timer is running that forces UI updates so that the duration of the active connection increases
    if (!hasValue(this.timerHandle) && this.filteredConnections.length > 0 && !hasValue(this.filteredConnections[0].endUtc)) {
      this.timerHandle = window.setInterval(() => {
        this.cdr.markForCheck();
      }, 5000);
    }

    this.isLoading = false;
    this.cdr.markForCheck();
  }

  private onInitListners(): void {
    // Listen to getDeviceConnectionsAction failed
    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.GET_DEVICE_CONNECTIONS_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.isLoading = false;
        this.cdr.markForCheck();
      });

    this.actionsListener$
      .pipe(ofType(DevicesActionsTypes.GET_DEVICE_CONNECTIONS_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.initData();
        this.cdr.markForCheck();
      });
  }

  public getDurationString(connection: ConnectionData): string{
    let duration: string = this.translate.instant('General.NotAvailableShort');

    if (connection.endUtc && connection.startUtc) {
      const uptime: number = (Date.parse(connection.endUtc) - Date.parse(connection.startUtc)) / 1000;
      duration = getDurationString(uptime, this.translate, DurationPrecision.Minutes);
    }
    else if (connection.startUtc) {
      const uptime: number = (Date.now() - Date.parse(connection.startUtc)) / 1000;
      duration = getDurationString(uptime, this.translate, DurationPrecision.Minutes);
    }

    return duration;
  }

  public getInterfaceModeString(): string {
    const key = `Modules.RemoteMaintenance.InterfaceModes.${getTranslationKeyForValue(this.xpac.networkInterface)}`;
    return this.translate.instant(key);
  }
}